// [PROSPEK] 2026-05-11 Sentry import for retry-exhaustion telemetry (issue #113)
import * as Sentry from '@sentry/vue';
// [/PROSPEK]
import { ref } from 'vue';

// [PROSPEK] 2026-05-11 URL sanitizer for retry-exhaustion telemetry (issue #113)
const describeUrlSafely = url => {
  try {
    const parsed = new URL(url);
    return {
      urlHost: parsed.host,
      urlPath: parsed.pathname,
      sasExpiresAt: parsed.searchParams.get('se') || undefined,
    };
  } catch {
    return { urlParseFailed: true };
  }
};
// [/PROSPEK]

// [PROSPEK] 2026-04-05 Add stale-request guards and resetState for delayed attachment URL replacement
export const useLoadWithRetry = (config = {}) => {
  const maxRetry = config.max_retry || 3;
  const backoff = config.backoff || 1000;

  const isLoaded = ref(false);
  const hasError = ref(false);
  let activeRequestId = 0;

  const resetState = () => {
    activeRequestId += 1;
    isLoaded.value = false;
    hasError.value = false;
  };

  const loadWithRetry = async url => {
    if (!url) {
      resetState();
      return;
    }

    const requestId = activeRequestId + 1;
    activeRequestId = requestId;
    isLoaded.value = false;
    hasError.value = false;

    const attemptLoad = () => {
      return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          if (requestId !== activeRequestId) {
            resolve();
            return;
          }
          isLoaded.value = true;
          hasError.value = false;
          resolve();
        };

        img.onerror = () => {
          if (requestId !== activeRequestId) {
            resolve();
            return;
          }
          reject(new Error('Failed to load image'));
        };

        img.src = url;
      });
    };

    const sleep = ms => {
      return new Promise(resolve => {
        setTimeout(resolve, ms);
      });
    };

    const retry = async (attempt = 0) => {
      try {
        await attemptLoad();
      } catch (error) {
        if (requestId !== activeRequestId) {
          return;
        }

        if (attempt + 1 >= maxRetry) {
          hasError.value = true;
          isLoaded.value = false;
          // [PROSPEK] 2026-05-11 Emit Sentry warning on retry exhaustion (issue #113)
          try {
            const desc = describeUrlSafely(url);
            Sentry.captureMessage('Image load retry exhausted', {
              level: 'warning',
              tags: { component: 'useLoadWithRetry' },
              extra: {
                ...desc,
                attempts: maxRetry,
              },
            });
          } catch (_) {
            // eslint-disable-next-line no-empty
          }
          // [/PROSPEK]
          return;
        }
        await sleep(backoff * (attempt + 1));
        await retry(attempt + 1);
      }
    };

    await retry();
  };

  return {
    isLoaded,
    hasError,
    loadWithRetry,
    resetState,
  };
};
// [/PROSPEK]
